import { css, html, TemplateResult } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { BaseElement } from "./base/BaseElement.js";

const styles = css`
  .slider-elements-wrapper {
    display: flex;
    width: 100%;
    height: 100%;

    will-change: transform;
  }

  .slider-elements-group {
    display: inline-flex;
    height: 100%;
    z-index: 10001;
    transition: transform 0.3s ease;
  }

  .slider-element-wrapper {
    flex-shrink: 0;
    flex-grow: 0;
    display: flex;
    flex-wrap: nowrap;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  .slider-elements-group-wrapper {
    width: 100%;
    overflow: hidden;
  }

  /* Slot içindeki elementlere stil verme */
  .slider-element-wrapper ::slotted(*) {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

@customElement("generic-slider")
export class Slider extends BaseElement {
  static override styles = styles;
  protected override useShadowRoot = true;

  @property({ type: Number, attribute: "current-index" })
  private _currentIndex = 0;

  @property({ type: Boolean, attribute: "touch-active" })
  private _touchActive = false;
  set touchActive(value: boolean) {
    this._touchActive = value;
    this.requestUpdate();
  }
  get touchActive() {
    return this._touchActive;
  }

  @property({ type: Number, attribute: "not-touch-active-index" })
  private _notThouchActiveIndex = -1;

  set notThouchActiveIndex(value: number) {
    this._notThouchActiveIndex = value;
    this.requestUpdate();
  }
  get notThouchActiveIndex() {
    return this._notThouchActiveIndex;
  }

  public get currentIndex(): number {
    return this._currentIndex;
  }

  private _elements: HTMLElement[] = [];

  private _imageTranslateX = 0;
  private _touchStartX = 0;
  private _touchStartY = 0;
  private _touchCurrentX = 0;
  private _isDragging = false;
  private _baseImageTranslateX = 0; // Base position before drag

  @query(".slider-elements-wrapper")
  private _elementsWrapperElement!: HTMLElement;

  @query(".slider-elements-group")
  private _elementsGroupElement!: HTMLElement;

  private _resizeObserver?: ResizeObserver;
  private _mutationObserver?: MutationObserver;

  override firstUpdated() {
    this._collectChildren();
    this._assignSlots();
    this._setupMutationObserver();

    // ResizeObserver'ı updateComplete sonrasında kur
    this.updateComplete.then(() => {
      this._setupResizeObserver();
      this._setupTransitionListener();
    });
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._cleanupObservers();
  }

  private _setupResizeObserver() {
    if (!this._elementsWrapperElement) return;

    // Eğer zaten bir observer varsa, önce disconnect et
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }

    this._resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Size değişikliği olduğunda pozisyonu güncelle
        this._handleSizeChange();
      }
    });

    this._resizeObserver.observe(this._elementsWrapperElement);
  }

  private _setupTransitionListener() {
    if (!this._elementsGroupElement) return;

    // transitionend event listener'ı ekle
    this._elementsGroupElement.addEventListener(
      "transitionend",
      this._handleTransitionEnd
    );
  }

  private _handleSizeChange() {
    // Wrapper size değiştiğinde pozisyonu güncelle
    this.requestUpdate();
    this.updateComplete.then(() => {
      this._updateElementPosition();
    });
  }

  private _setupMutationObserver() {
    this._mutationObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;

      for (const mutation of mutations) {
        // Child ekleme/çıkarma
        if (mutation.type === "childList") {
          shouldUpdate = true;
          break;
        }
        // Attribute değişiklikleri (örneğin slot attribute'u)
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "slot"
        ) {
          shouldUpdate = true;
          break;
        }
      }

      if (shouldUpdate) {
        this._handleChildChange();
      }
    });

    // Light DOM'daki child değişikliklerini izle
    this._mutationObserver.observe(this, {
      childList: true,
      subtree: false, // Sadece direkt child'ları izle
      attributes: true,
      attributeFilter: ["slot"],
    });
  }

  private _handleChildChange() {
    // Child değişikliği olduğunda children'ları yeniden topla
    this._collectChildren();
    this._assignSlots();
  }

  private _cleanupObservers() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = undefined;
    }

    if (this._mutationObserver) {
      this._mutationObserver.disconnect();
      this._mutationObserver = undefined;
    }

    // transitionend event listener'ı temizle
    if (this._elementsGroupElement) {
      this._elementsGroupElement.removeEventListener(
        "transitionend",
        this._handleTransitionEnd
      );
    }
  }

  override updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);
    this.updateComplete.then(() => {
      this._assignSlots();

      // ResizeObserver henüz kurulmadıysa kur
      if (!this._resizeObserver && this._elementsWrapperElement) {
        this._setupResizeObserver();
      }
    });
  }

  private _collectChildren() {
    // Light DOM'daki children'ları topla
    this._elements = Array.from(this.children).filter(
      (el): el is HTMLElement => el instanceof HTMLElement
    );
    this._updateAfterCollection();
  }

  private _assignSlots() {
    // Her elemente slot attribute'u ekle
    this._elements.forEach((element, index) => {
      element.setAttribute("slot", `element-${index}`);
    });
  }

  private _updateAfterCollection() {
    // Index'i sınırla
    if (this._currentIndex >= this._elements.length && this._elements.length > 0) {
      this._currentIndex = Math.max(0, this._elements.length - 1);
    }

    // Position'ı güncelle
    if (this._elements.length > 0) {
      this.requestUpdate();
      this.updateComplete.then(() => {
        this._assignSlots();
        this._updateElementPosition();
      });
    }
  }

  private _handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      this._prevElement();
    } else if (e.key === "ArrowRight") {
      this._nextElement();
    }
  };

  private _nextElement() {
    this._currentIndex++;
    this.fixSelectedIndex();
    this.requestUpdate();
    this._updateElementPosition();
  }

  public nextElement() {
    this._nextElement();
  }

  public previousElement() {
    this._prevElement();
  }

  private _prevElement() {
    this._currentIndex--;
    this.fixSelectedIndex();
    this.requestUpdate();
    this._updateElementPosition();
  }

  public selectElement(index: number) {
    this._selectElement(index);
  }

  private fixSelectedIndex() {
    const container = this._elementsWrapperElement!;
    const actualContainer = this._elementsGroupElement!;

    // İlk slot'tan wrapper'ı bul
    const firstSlot = this.shadowRoot?.querySelector(
      `slot[name="element-0"]`
    ) as HTMLSlotElement;
    const firstWrapper = firstSlot?.parentElement as HTMLElement;
    const wrapperWidth = firstWrapper?.offsetWidth ?? 0;

    const internalIndexCount =
      wrapperWidth > 0
        ? Math.ceil(
            (actualContainer.scrollWidth - container.offsetWidth) / wrapperWidth
          )
        : 0;

    if (this._currentIndex > internalIndexCount) {
      this._currentIndex = 0;
    }
    if (this._currentIndex < 0) {
      this._currentIndex = internalIndexCount - 1;
    }
  }

  private _selectElement(index: number) {
    if (index >= 0 && index < this._elements.length) {
      this._currentIndex = index;
      this.fixSelectedIndex();
      this.requestUpdate();
      this._updateElementPosition();
    }
  }

  private _updateElementPosition() {
    // Update image container position based on current index
    this.updateComplete.then(() => {
      // Slot'tan wrapper'ı bul
      const slot = this.shadowRoot?.querySelector(
        `slot[name="element-${this._currentIndex}"]`
      ) as HTMLSlotElement;
      const container = slot?.parentElement as HTMLElement;

      if (container && this._elements.length > 0) {
        const containerWidth = container.offsetWidth;
        // Move container so current element is at the start (left: 0)
        this._imageTranslateX = -this._currentIndex * containerWidth;
        this._elementsGroupElement.style.transform = `translateX(${this._imageTranslateX}px)`;
      }
    });
  }

  private _handleTransitionEnd = (e: TransitionEvent) => {
    // Sadece transform property'si için event fırlat
    if (e.propertyName === "transform") {
      this.dispatchEvent(
        new CustomEvent("slider-transition-end", {
          detail: {
            currentIndex: this._currentIndex,
            translateX: this._imageTranslateX,
          },
          bubbles: true,
          composed: true,
        })
      );
    }
  };

  private _handleTouchStart = (e: TouchEvent) => {
    if (e.touches.length === 1) {
      this._touchStartX = e.touches[0].clientX;
      this._touchStartY = e.touches[0].clientY;
      this._isDragging = false;

      // Save current base position immediately
      // Slot'tan wrapper'ı bul
      const slot = this.shadowRoot?.querySelector(
        `slot[name="element-${this._currentIndex}"]`
      ) as HTMLSlotElement;
      const container = slot?.parentElement as HTMLElement;
      if (container) {
        const containerWidth = container.offsetWidth;
        this._baseImageTranslateX = -this._currentIndex * containerWidth;
      } else {
        // Fallback if container not found
        this._baseImageTranslateX = this._imageTranslateX;
      }
    }
  };

  private _handleTouchMove = (e: TouchEvent) => {
    if (e.touches.length === 1) {
      this._touchCurrentX = e.touches[0].clientX;
      const touchCurrentY = e.touches[0].clientY;

      const deltaX = Math.abs(this._touchCurrentX - this._touchStartX);
      const deltaY = Math.abs(touchCurrentY - this._touchStartY);

      // Only start dragging if horizontal movement is greater than vertical
      if (deltaX > 5 && deltaX > deltaY) {
        if (!this._isDragging) {
          this._isDragging = true;
          // Disable transition during drag for smooth real-time movement
          this._elementsGroupElement.style.transition = "none";
        }

        e.preventDefault();

        // Calculate drag offset
        const dragDeltaX = this._touchCurrentX - this._touchStartX;
        const newTranslateX = this._baseImageTranslateX + dragDeltaX;

        // Apply transform in real-time
        this._elementsGroupElement.style.transform = `translateX(${newTranslateX}px)`;
      }
    }
  };

  private _handleTouchEnd = (e: TouchEvent) => {
    if (this._isDragging) {
      // Re-enable transition
      this._elementsGroupElement.style.transition = "transform 0.3s ease";

      const deltaX = this._touchCurrentX - this._touchStartX;
      // Slot'tan wrapper'ı bul
      const slot = this.shadowRoot?.querySelector(
        `slot[name="element-${this._currentIndex}"]`
      ) as HTMLSlotElement;
      const container = slot?.parentElement as HTMLElement;
      const containerWidth = container?.offsetWidth ?? 0;
      const swipeThreshold = containerWidth ? containerWidth / 2 : 0; // Minimum distance for swipe

      if (Math.abs(deltaX) > swipeThreshold) {
        if (deltaX > 0) {
          // Swipe right - previous element
          this._prevElement();
        } else {
          // Swipe left - next element
          this._nextElement();
        }
      } else {
        // Snap back to current element if threshold not met
        this._updateElementPosition();
      }

      this._isDragging = false;
    }
  };

  protected renderHtml(): TemplateResult {
    const elementCount = this._elements.length;

    return html`
      ${
        /*elementCount > 1
          ? html`
              <button
                class="slider-nav-button prev"
                @click=${this._prevElement}
              ></button>
              <button
                class="slider-nav-button next"
                @click=${this._nextElement}
              ></button>
            `
          : ""*/ ""
      }
      <div class="slider-elements-wrapper">
        <div class="slider-elements-group-wrapper">
          ${elementCount > 0
            ? html`
                 <div class="slider-elements-group">    
                    ${this._elements.map(
                      (element, index) => html`
                       
                          <div style="width:${
                            this._elementsWrapperElement.offsetWidth
                          }px" class="slider-element-wrapper ${
                        index === this._currentIndex ? "active" : ""
                      }"
                          @click=${() => {
                            this._selectElement(index);
                          }}

                           @touchstart=${(e: TouchEvent) => {
                             this._notThouchActiveIndex !== index
                               ? this._handleTouchStart(e)
                               : undefined;
                           }}
                           
                          @touchmove=${(e: TouchEvent) => {
                            this._notThouchActiveIndex !== index
                              ? this._handleTouchMove(e)
                              : undefined;
                          }}
                          @touchend=${(e: TouchEvent) => {
                            this._notThouchActiveIndex !== index
                              ? this._handleTouchEnd(e)
                              : undefined;
                          }}
                         >
                           <slot name="element-${index}"></slot>
                         </div>
                        </div>
                      `
                    )}
                    </div>
              </div>
            </div>
          </div>
          `
            : ""}
        </div>
      </div>
    `;
  }
}
